// Single source of truth for the Sales Hub. Edit here when collateral changes —
// content is rendered by src/pages/SalesHub.jsx. Sourced from:
//   • B1S Managed IT Battle Card (2026)
//   • Managed IT Services Value Proposition (May 2024)
//   • Customer references (ISTC, KELIN, Rhombus)
//   • binaryone.co.ke

export const COMPANY = {
  name: 'Binary One Solutions Ltd',
  shortName: 'Binary One',
  slogan: 'Delivering business value with professional simplicity through proven technical expertise.',
  positioning:
    'We are not IT fixers. We are your long-term digital transformation partner. Start with Managed IT; graduate to full digital excellence.',
  address: 'St. Charles Lwanga House, Ngong Road, Nairobi',
  phone: '+254 787 990 220',
  email: 'info@binaryone.co.ke',
  website: 'https://binaryone.co.ke',
  yearsExperience: '30+ years cumulative IT expertise',
  cta: 'Let us do a free, no-obligation IT audit. We\'ll show you exactly where the gaps are — and what they\'re costing you.'
};

// Three Managed IT retainer tiers (battle card, 2026)
export const PRICING_TIERS = [
  {
    name: 'Silver',
    monthlyKES: 125000,
    headline: '2 days/wk on-site + remote + vCIO',
    bullets: [
      '2 on-site days per week (allocated technician)',
      '1 hour/day remote support on non-service days',
      'Fortnightly Virtual CIO advisory',
      '24/7 IT Help Desk ticketing system',
      'Initial vCIO-led IT Audit & Risk Register'
    ],
    bestFor: 'Smaller offices (20–40 staff) starting their managed-IT journey.'
  },
  {
    name: 'Gold',
    monthlyKES: 150000,
    headline: '3 days/wk + ERP advisory + backups',
    bullets: [
      '3 on-site days per week',
      '2 hours/day remote support',
      'Fortnightly Virtual CIO advisory',
      'ERP/Odoo advisory included',
      'Managed backups (cloud + on-prem)'
    ],
    bestFor: 'Growing SMEs (40–80 staff) with ERP plans on the horizon.',
    recommended: true
  },
  {
    name: 'Platinum',
    monthlyKES: 200000,
    monthlyKESLabel: 'KES 200,000+',
    headline: 'Full-time + Odoo/Clio PoC + training',
    bullets: [
      '5 on-site days per week (full-time technician)',
      'Dedicated remote IT Supervisor',
      'Odoo or Clio Proof-of-Concept included',
      'Structured end-user training programme',
      'Disaster recovery plan & quarterly DR drills'
    ],
    bestFor: 'Mid-market (80+ staff) needing a fully outsourced IT function.'
  }
];

// 11 New-client value-adds bundled into every retainer (battle card)
export const VALUE_ADDS = [
  { title: 'vCIO-Led IT Audit & Risk Register',
    body: 'We audit your entire IT environment and deliver a formal Risk Register mapping every gap into prioritised projects.' },
  { title: 'Professional IT Help Desk',
    body: 'A real ticketing system — not WhatsApp. Log, track, and escalate issues. Monthly management reports included.' },
  { title: 'Microsoft 365 Empowerment',
    body: 'Training and optimisation of Teams, SharePoint, OneDrive, Outlook. Your staff become productive, not just connected.' },
  { title: 'Free Odoo Module (CRM or Leave)',
    body: 'Choose one complimentary Odoo module: CRM for pipeline management or Online Leave for HR. Setup and training included.' },
  { title: 'Cybersecurity Hardening',
    body: 'Network segmentation (VLANs), Sophos firewalls, endpoint protection. We move you from reactive to proactive security.' },
  { title: 'Server Migration to Linux',
    body: 'Eliminate expensive Windows Server licensing. We migrate to Linux where appropriate — cutting costs immediately.' },
  { title: 'Data Protection Guarantee',
    body: 'Backup verification, access controls, encryption policies, disaster recovery plan. A formal Data Protection Action Plan.' },
  { title: 'In-House Telephony + IVR',
    body: 'Affordable VoIP with auto-response and IVR. Professional phone handling from Day 1, replacing expensive PBX systems.' },
  { title: 'Free Website Revamp',
    body: 'A professional website refresh in your first 60 days. Visible proof of partnership your board can see.' },
  { title: 'AI Empowerment & Training',
    body: 'Hands-on training with Claude Cowork and Microsoft Copilot. Your team learns to use AI for real business tasks.' },
  { title: 'End-User Training',
    body: 'Structured sessions on M365 productivity, ERP modules, and cybersecurity awareness. Reduces tickets, increases adoption.' }
];

// Ideal Client Profile (battle card)
export const ICP = [
  { segment: 'Law Firms (20–60 staff)',         hook: 'Case management gaps, compliance needs, Clio/Wakili expertise.' },
  { segment: 'NGOs / Missions (30–100)',        hook: 'Donor reporting, compliance, limited in-house IT.' },
  { segment: 'Manufacturing SMEs (30–100)',     hook: 'Fragmented systems, ERP-curious, need reliable support.' },
  { segment: 'Real Estate / SACCOs',            hook: 'Legacy systems, growing digital needs.' },
  { segment: 'Professional Services',           hook: 'IT-dependent, often underserved by current providers.' }
];

// Public client references for proof
export const TRUSTED_BY = [
  'Ahmednasir Abdullahi Advocates LLP',
  'KN Law LLP',
  'Pacific Petroleum (Dubai & East Africa)',
  'International Safety & Training Centre',
  'Vivo Fashion Group',
  'Rhombus Concrete',
  'African Eagle Tour Operator'
];

// Objections (battle card)
export const OBJECTIONS = [
  {
    objection: '"We already have an IT guy."',
    response: 'We complement your team — no sick-leave gaps, no knowledge silos, plus a strategic vCIO. Your IT person handles the day-to-day; we cover everything else and elevate the function.'
  },
  {
    objection: '"You\'re too expensive."',
    response: 'Compare it to the cost of one full-time IT hire (KES 150K+ in salary alone, before benefits, leave, recruitment) — plus the cost of a single day of downtime. Our retainer is predictable and almost always cheaper.'
  },
  {
    objection: '"We don\'t need ERP."',
    response: 'That\'s fine — we don\'t push it. We advise early so when you\'re ready, the roadmap exists. Free advisory is included in every retainer.'
  },
  {
    objection: '"Our current provider is OK."',
    response: 'Great — let\'s do a free IT audit so you can benchmark them. No obligation, just clarity. If they\'re doing well, you\'ll have proof. If they\'re not, you\'ll have a plan.'
  },
  {
    objection: '"We\'re not ready right now."',
    response: 'Understood. Can we schedule the free audit for [next quarter] so it doesn\'t fall off your radar? It costs you nothing and gives you a baseline before your next budget cycle.'
  }
];

// Email & call templates. Variables in {{double_braces}} are replaced live in the UI.
export const EMAIL_TEMPLATES = [
  {
    id: 'cold-generic',
    name: 'Cold outreach — generic SME',
    subject: '{{first_name}}, can I send you a free IT audit for {{company}}?',
    body:
`Hi {{first_name}},

I'm {{your_name}} from Binary One Solutions — a Nairobi-based ICT consulting firm with 30+ years of cumulative IT expertise. We run the outsourced IT function for organisations like KN Law LLP, ISTC, and Rhombus Concrete.

I noticed {{company}} is in the {{industry}} space and growing — and that's usually where IT becomes a bottleneck (downtime, M365 gaps, weak backups, no clear strategy).

We do a one-time, no-obligation IT audit that delivers a formal Risk Register — every gap mapped to a prioritised project. Most clients use it as a benchmark even if they don't engage us.

Would Tuesday or Thursday next week work for a 20-minute discovery call?

Best,
{{your_name}}
Binary One Solutions
+254 787 990 220 · binaryone.co.ke`
  },
  {
    id: 'cold-law',
    name: 'Cold outreach — Law firm',
    subject: 'Clio/Wakili readiness audit for {{company}}',
    body:
`Hi {{first_name}},

We work with several leading law firms — Ahmednasir Abdullahi Advocates LLP and KN Law LLP among them — handling their IT, M365, case-management readiness (Clio / Wakili), and the compliance side that comes with handling client data in Kenya.

I'd like to offer {{company}} a complimentary Legal IT Audit. Outputs:

  • Risk Register mapped to LSK, Data Protection Act 2019, and ISO 27001 expectations
  • Microsoft 365 / SharePoint maturity score for matter management
  • Clio / Wakili readiness assessment if you're considering case management
  • Estimated 12-month TCO vs. your current setup

No charge, no obligation. Could we schedule a 20-minute discovery call next week?

Warm regards,
{{your_name}}
Binary One Solutions
+254 787 990 220 · binaryone.co.ke`
  },
  {
    id: 'cold-ngo',
    name: 'Cold outreach — NGO / Mission',
    subject: 'Donor-grade IT support for {{company}} — at NGO pricing',
    body:
`Dear {{first_name}},

NGOs we partner with — including International Safety & Training Centre — have one thing in common: donor reporting demands a level of IT discipline (data protection, audit trails, secure access) that's hard to maintain with limited in-house IT.

Binary One offers a fully outsourced IT function that includes:

  • Strategic Virtual CIO engagement (governance + donor-ready policies)
  • IT Help Desk with monthly management reports
  • Cybersecurity hardening + Data Protection Action Plan
  • Microsoft 365 empowerment and end-user training

I'd like to offer {{company}} a free IT audit and Total Cost of Ownership analysis — no obligation. The output is something you can share with your board or donors.

Would next week work for a 20-minute call?

Best,
{{your_name}}
Binary One Solutions
+254 787 990 220 · binaryone.co.ke`
  },
  {
    id: 'cold-manufacturer',
    name: 'Cold outreach — Manufacturing SME',
    subject: 'IT and ERP readiness review for {{company}}',
    body:
`Hi {{first_name}},

We help manufacturing SMEs like Rhombus Concrete and Vivo Fashion Group consolidate fragmented IT — multiple support vendors, ageing servers, M365 confusion, and no clear ERP path.

Binary One offers a free IT and ERP-readiness audit that gives you:

  • A Risk Register tied to your production systems and continuity needs
  • Linux server migration savings analysis (replace Windows Server licensing)
  • Odoo readiness report — modules, phasing, indicative effort
  • A 12-month roadmap costed against your current spend

Could I schedule a 20-minute discovery call next week?

Best,
{{your_name}}
Binary One Solutions
+254 787 990 220 · binaryone.co.ke`
  },
  {
    id: 'follow-up',
    name: 'Follow-up — no response (3rd touch)',
    subject: 'Re: {{previous_subject}}',
    body:
`Hi {{first_name}},

Bumping this in case it slipped through. Happy to make it easier — pick whichever's most useful and I'll send a one-pager:

  1. Managed IT Services — pricing & inclusions
  2. vCIO Value Proposition (what it actually looks like in practice)
  3. A short customer reference (KN Law / ISTC / Rhombus)

Or if you'd rather just chat for 15 minutes, here's my calendar: [your_calendar_link].

Best,
{{your_name}}
Binary One Solutions`
  },
  {
    id: 'audit-booking',
    name: 'Confirm — free IT audit booked',
    subject: 'Confirmed: Free IT audit at {{company}} — {{audit_date}}',
    body:
`Hi {{first_name}},

Confirming our free IT audit at {{company}} on {{audit_date}}.

What we'll do (3–4 hours on-site):

  1. Walk-through of your IT environment — servers, network, endpoints, M365 setup, backups
  2. Brief interviews with 2–3 key staff (10 min each) on pain points
  3. Review of any existing IT vendor contracts and SLAs

What you'll get back within 5 working days:

  • A formal Risk Register (every gap, prioritised)
  • Total Cost of Ownership analysis vs. comparable Managed IT engagement
  • A pragmatic 12-month roadmap

No obligation, no follow-up sales pressure unless you ask for it. Looking forward to it.

Best,
{{your_name}}
Binary One Solutions
+254 787 990 220 · binaryone.co.ke`
  }
];

// Cold call & voicemail scripts
export const CALL_SCRIPTS = [
  {
    id: 'opening-15s',
    name: '15-second opener (cold call)',
    body:
`"Hi {{first_name}}, this is {{your_name}} from Binary One Solutions — we run the outsourced IT for KN Law, ISTC, and Rhombus Concrete here in Nairobi.

The reason for my call: we offer a free IT audit that gives you a formal Risk Register and a 12-month roadmap. Most {{industry_segment}} of your size find it useful as a benchmark even if they don't engage us.

Could I take 90 seconds to explain how it works?"`
  },
  {
    id: 'discovery-call',
    name: 'Discovery call — agenda (20 min)',
    body:
`Open (1 min)
  • Thank them for the time. Confirm you have 20 minutes.

Their world (8 min)
  • "Walk me through how IT is set up at {{company}} today — in-house, outsourced, hybrid?"
  • "What's working well? What's the recurring source of pain?"
  • "When was the last time you had a serious incident — outage, security scare, data loss?"
  • "What's on your IT wish-list for the next 12 months?"

Tee-up B1S (5 min) — do not pitch. Reflect what you heard.
  • "Based on what you've described, the audit would zero in on [their stated pain]. Here's what it covers..."
  • Briefly: vCIO Audit, Risk Register, TCO, 12-month roadmap.
  • Reference one similar client by name + the result.

Close (5 min)
  • "Two next steps: I can either send the audit one-pager and a couple of customer references, OR we book the audit itself — 3–4 hours on-site, no charge, full report in 5 working days. Which is more useful?"
  • Lock the date in the calendar before ending the call.
  • Confirm decision-makers who should be in the readout.

Notes
  • Capture: stack, headcount, current IT spend, current vendor SLA, top 3 pain points.
  • Log all of this into the CRM lead record before EOD.`
  },
  {
    id: 'voicemail',
    name: 'Voicemail — 20 seconds',
    body:
`"Hi {{first_name}}, {{your_name}} here from Binary One Solutions — we handle outsourced IT for several law firms and SMEs across Nairobi. I was calling to offer {{company}} a free, no-obligation IT audit.

I'll send a quick email to follow up. If easier, you can reach me at +254 787 990 220. Thanks, {{first_name}}."`
  },
  {
    id: 'gatekeeper',
    name: 'Gatekeeper — getting to the decision-maker',
    body:
`"Hi, this is {{your_name}} from Binary One Solutions. Could you point me to the person responsible for IT or technology decisions at {{company}}? I'm not selling — we offer a free IT audit several of your peers have used. Who'd be the right person to send the one-pager to?"

Variations:
  • If asked "what's it about" → "A free IT audit. We do them for SMEs your size and the output is a Risk Register and a 12-month roadmap. No obligation."
  • If asked to email → "Happy to. What's the best email for the IT lead — or your operations director if IT reports there?"
  • If brushed off → "No problem. Can I send you a one-pager and try back next Tuesday?"`
  }
];

// Quick stats for the hero band
export const QUICK_FACTS = [
  { label: 'Years of cumulative IT expertise', value: '30+' },
  { label: 'Managed IT tiers',                   value: '3' },
  { label: 'Value-adds in every retainer',       value: '11' },
  { label: 'Uptime SLA',                          value: '99%' }
];
